'use client';

import { useGetWatchList } from '@/queris';
import { OfferType } from '@/types';
import { useTranslations } from 'next-intl';
import { FC, useCallback, useState } from 'react';
import { Container, List } from './styled';
import { Typography } from '@mui/material';
import { COLORS } from '@/constants';
import {
  BuildingIcon,
  CustomPagination,
  HouseKeyIcon,
  HousingCard,
  IconToggleGroup,
  NoData,
} from '@/components';
import { WatchListSkeleton } from './WatchListSkeleton';

type WatchListTab = { kind: 'offerType'; offerType: OfferType } | { kind: 'projects' };

const SELL_TAB: WatchListTab = { kind: 'offerType', offerType: OfferType.SELL };
const RENT_TAB: WatchListTab = { kind: 'offerType', offerType: OfferType.RENT };
const PROJECTS_TAB: WatchListTab = { kind: 'projects' };

export const WatchListScreen: FC = () => {
  const t = useTranslations();

  const [chosenTab, setChosenTab] = useState<WatchListTab>(SELL_TAB);
  const [sellPage, setSellPage] = useState(0);
  const [rentPage, setRentPage] = useState(0);
  const [projectsPage, setProjectsPage] = useState(0);

  const sellQuery = useGetWatchList({ offerType: OfferType.SELL, page: sellPage, project: false });
  const rentQuery = useGetWatchList({ offerType: OfferType.RENT, page: rentPage, project: false });
  const projectsQuery = useGetWatchList({ page: projectsPage, project: true });

  const activeQuery =
    chosenTab.kind === 'projects'
      ? projectsQuery
      : chosenTab.offerType === OfferType.SELL
        ? sellQuery
        : rentQuery;

  const activePage =
    chosenTab.kind === 'projects'
      ? projectsPage
      : chosenTab.offerType === OfferType.SELL
        ? sellPage
        : rentPage;

  const setActivePage =
    chosenTab.kind === 'projects'
      ? setProjectsPage
      : chosenTab.offerType === OfferType.SELL
        ? setSellPage
        : setRentPage;

  const { data, isLoading } = activeQuery;
  const objects = data?.objects ?? [];

  const handleChangeTab = useCallback((value: WatchListTab[]) => {
    if (value.length === 0) return;
    setChosenTab(value[value.length - 1]);
  }, []);

  const isTabEqual = (a: WatchListTab, b: WatchListTab) => {
    if (a.kind !== b.kind) return false;
    if (a.kind === 'offerType' && b.kind === 'offerType') return a.offerType === b.offerType;
    return true;
  };

  return (
    <Container>
      <Typography color={COLORS['primary/900']} fontSize={30} fontWeight={700} lineHeight={1}>
        {t('navigation.watch-list')}
      </Typography>

      <IconToggleGroup<WatchListTab>
        options={[
          {
            value: SELL_TAB,
            title: t('sell'),
            count: sellQuery.data?.numFound || undefined,
          },
          {
            value: RENT_TAB,
            title: t('rent'),
            count: rentQuery.data?.numFound || undefined,
            icon: <HouseKeyIcon />,
          },
          {
            value: PROJECTS_TAB,
            title: t('watchList.projects'),
            count: projectsQuery.data?.numFound || undefined,
            icon: <BuildingIcon />,
          },
        ]}
        selected={[chosenTab]}
        onChange={handleChangeTab}
        isEqual={isTabEqual}
      />

      {isLoading ? (
        <WatchListSkeleton />
      ) : objects.length > 0 ? (
        <>
          <List>
            {objects.map((item) => (
              <HousingCard
                key={item.id}
                data={item}
                showRequests
                offerType={chosenTab.kind === 'offerType' ? chosenTab.offerType : undefined}
                isDeactivated={!item.active}
                isProject={chosenTab.kind === 'projects'}
              />
            ))}
          </List>
          <CustomPagination
            page={activePage}
            numFound={data?.numFound}
            itemsPerPage={10}
            onPageChange={setActivePage}
          />
        </>
      ) : (
        <NoData message={t('watchList.noData')} />
      )}
    </Container>
  );
};
